import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook for URL-based filter persistence.
 * Stores filter state in URL query parameters, enabling bookmarking,
 * sharing, and proper browser back/forward navigation.
 *
 * @param {Object} defaultFilters - Filter names and default values, e.g. { search: '', difficulty: 'all', page: 1 }
 * @param {Object} [options] - Configuration
 * @param {Object} [options.debounce] - Keys to debounce with delay in ms, e.g. { search: 300 }
 * @param {boolean} [options.enabled=true] - Whether URL sync is active (useful for conditional views)
 * @returns {{ filters: Object, debouncedFilters: Object, setFilter: Function, clearFilters: Function }}
 */
export function useUrlFilters(defaultFilters, options = {}) {
  const { debounce: debounceConfig = {}, enabled = true } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const isSyncingFromUrl = useRef(false);
  const defaultsRef = useRef(defaultFilters);
  const debounceKeysRef = useRef(Object.keys(debounceConfig));
  const debounceMs = useMemo(
    () => Object.values(debounceConfig).length > 0 ? Math.max(...Object.values(debounceConfig)) : 0,
    []
  );

  const types = useMemo(() => {
    const t = {};
    for (const [key, val] of Object.entries(defaultFilters)) {
      t[key] = typeof val === 'number' ? 'number' : 'string';
    }
    return t;
  }, []);

  const readUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const result = {};
    for (const [key, defaultVal] of Object.entries(defaultsRef.current)) {
      const raw = params.get(key);
      if (raw === null) {
        result[key] = defaultVal;
      } else if (types[key] === 'number') {
        result[key] = parseInt(raw, 10) || defaultVal;
      } else {
        result[key] = raw;
      }
    }
    return result;
  }, [location.search, types]);

  const [filters, setFiltersState] = useState(readUrl);
  const [debouncedFilters, setDebouncedFilters] = useState(readUrl);

  const writeUrl = useCallback((vals) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(vals)) {
      const defaultVal = defaultsRef.current[key];
      if (value !== defaultVal && value !== '' && value !== null && value !== undefined) {
        params.set(key, String(value));
      }
    }
    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, '');
    if (newSearch !== currentSearch) {
      navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
    }
  }, [navigate, location.pathname, location.search]);

  // Sync state from URL on back/forward navigation
  useEffect(() => {
    if (!enabled) return;
    isSyncingFromUrl.current = true;
    const vals = readUrl();
    setFiltersState(vals);
    setDebouncedFilters(vals);
    Promise.resolve().then(() => { isSyncingFromUrl.current = false; });
  }, [location.search, readUrl, enabled]);

  // Handle debouncing and immediate filter sync
  useEffect(() => {
    if (isSyncingFromUrl.current || !enabled) return;
    const debounceKeys = debounceKeysRef.current;

    // Immediately sync non-debounced keys to debouncedFilters
    setDebouncedFilters(prev => {
      const next = { ...prev };
      let changed = false;
      for (const [key, val] of Object.entries(filters)) {
        if (!debounceKeys.includes(key) && prev[key] !== val) {
          next[key] = val;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    if (debounceKeys.length === 0 || debounceMs === 0) return;

    const timer = setTimeout(() => {
      setDebouncedFilters(prev => {
        const next = { ...prev };
        let changed = false;
        for (const key of debounceKeys) {
          if (prev[key] !== filters[key]) {
            next[key] = filters[key];
            changed = true;
          }
        }
        if (changed && 'page' in next) {
          next.page = defaultsRef.current.page;
          setFiltersState(p => ({ ...p, page: defaultsRef.current.page }));
        }
        return changed ? next : prev;
      });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [filters, enabled, debounceMs]);

  // Update URL when debounced filters change
  useEffect(() => {
    if (isSyncingFromUrl.current || !enabled) return;
    writeUrl(debouncedFilters);
  }, [debouncedFilters, writeUrl, enabled]);

  const setFilter = useCallback((keyOrObj, value) => {
    setFiltersState(prev => {
      if (typeof keyOrObj === 'object') return { ...prev, ...keyOrObj };
      const resolvedValue = typeof value === 'function' ? value(prev[keyOrObj]) : value;
      return { ...prev, [keyOrObj]: resolvedValue };
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({ ...defaultsRef.current });
    setDebouncedFilters({ ...defaultsRef.current });
  }, []);

  return { filters, debouncedFilters, setFilter, clearFilters };
}
